import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';
import { dateStringOrInstant } from '../zod-utils.js';
import {
  assessmentResultSchema,
  assessmentWithContentSchema,
  updateAssessmentResultOptionsSchema,
} from './assessments.js';
import { lessonKindSchema, lessonSchema } from './lesson.js';
import {
  DEPRECATED_pagingSchemaWithMaxPageSize,
  filteringSchemaFrom,
  largePagingSchema,
  pagingSchema,
} from './modifier.js';
import { SessionFeedbackIssue, sessionFeedbackIssueSchema } from './session-feedback-issue.js';
import { sessionZoomSchema } from './session-zoom.js';
import { coachViewableStudentSchema, studentYearSchema } from './student.js';
import { userSchema, userWithRolesAndProfilesSchema } from './user.js';
import { paginatedResponse } from './util-schema-types.js';

export const completedGLADateEntrySchema = z.object({
  sessionStart: dateStringOrInstant,
  wpm: z.number(),
  accuracyPercent: z.number(),
  sessionId: z.string(),
  coachId: z.string(),
});
export type CompletedGLADateEntry = z.infer<typeof completedGLADateEntrySchema>;

export {
  recordedStudentSessionIssues,
  SessionFeedbackIssue,
  sessionFeedbackIssueSchema,
  studentSessionFeedbackIssues,
} from './session-feedback-issue.js';

export const sessionFeedbackLevelSchema = z.enum(['Too_easy', 'Too_hard']);
export type SessionFeedbackLevel = z.infer<typeof sessionFeedbackLevelSchema>;
export const SessionFeedbackLevel = sessionFeedbackLevelSchema.enum;

export const onDemandGLARecordedStatusSchema = z.enum(['recorded', 'under_review', 'not_recorded']);
export type OnDemandGLARecordedStatus = z.infer<typeof onDemandGLARecordedStatusSchema>;
export const OnDemandGLARecordedStatus = onDemandGLARecordedStatusSchema.enum;

export const plainSessionFeedbackSchema = z.object({
  sessionId: z.string(),
  coachId: z.string(),
  studentId: z.string(),
  feedbackContent: z.string().nullable(),
  majorConcerns: z.string().nullable(),
  otherTechIssues: z.string().nullable(),
  studentAttended: z.boolean().nullable(),
  feedbackSubmitted: z.boolean(),
  lateFeedbackDismissed: z.boolean().nullable(),
  issues: sessionFeedbackIssueSchema.array(),
  levels: sessionFeedbackLevelSchema.array(),
  submittedAtInstant: z.instanceof(Temporal.Instant).nullable(),
  draftSavedAtInstant: z.instanceof(Temporal.Instant).nullable(),
  submittedHoursFromSessionEnd: z.number().nullable(),
  dailySummary: z.string().nullable(),
});
export type PlainSessionFeedback = z.infer<typeof plainSessionFeedbackSchema>;

export const sessionFeedbackSchema = plainSessionFeedbackSchema.extend({
  coach: z.lazy(() => userSchema),
  student: z.lazy(() => userWithRolesAndProfilesSchema),
});
export type SessionFeedback = z.infer<typeof sessionFeedbackSchema>;

export const sessionFeedbackWithAssessmentSchema = sessionFeedbackSchema.extend({
  assessmentResults: z.array(assessmentResultSchema),
  sessionId: z.string(),
  coachId: z.string(),
  studentId: z.string(),
  session: z.object({
    id: z.string(),
    startInstant: z.instanceof(Temporal.Instant),
    endInstant: z.instanceof(Temporal.Instant),
    canceledAtInstant: z.instanceof(Temporal.Instant).nullable(),
    hoursCanceledBeforeStart: z.number().nullable(),
    lesson: lessonSchema,
    lessonLink: z.string().nullable(),
    zoom: sessionZoomSchema.nullable(),
    originalCoach: z.lazy(() => userSchema).nullable(),
  }),
  assessmentFinished: z.boolean().nullable(),
  gradeLevelWPM: z.number().nullable(),
  gradeLevelAccuracy: z.number().nullable(),
  progressMonitorWPM: z.number().nullable(),
  progressMonitorAccuracy: z.number().nullable(),
  relevantAssessments: z.array(assessmentWithContentSchema),
  /**
   * Makeup diagnostic resume: assessment content IDs completed in a prior incomplete diagnostic.
   * Empty/omitted for non-makeup sessions.
   */
  diagnosticContentIdsCompletedInPriorSession: z.array(z.string()).optional(),
});

export type SessionFeedbackWithAssessment = z.infer<typeof sessionFeedbackWithAssessmentSchema>;

export const sessionFeedbacksWithAssessmentSchema = paginatedResponse.extend({
  sessionFeedbacks: z.array(sessionFeedbackWithAssessmentSchema),
});

export type SessionFeedbacksWithAssessment = z.infer<typeof sessionFeedbacksWithAssessmentSchema>;

export const sessionFeedbackActionSchema = z.enum(['submit', 'saveAsDraft', 'adminManualAction']);
export type SessionFeedbackAction = z.infer<typeof sessionFeedbackActionSchema>;
export const SessionFeedbackAction = sessionFeedbackActionSchema.enum;

export const updateSessionFeedbackOptionsSchema = z.object({
  sessionId: z.string(),
  studentId: z.string(),
  coachId: z.string(),
  feedbackContent: z.string().nullable().optional(),
  majorConcerns: z.string().nullable().optional(),
  otherTechIssues: z.string().nullable().optional(),
  issues: sessionFeedbackIssueSchema.array().optional(),
  lateFeedbackDismissed: z.boolean().nullable().optional(),
  studentAttended: z.boolean().nullable().optional(),
  action: sessionFeedbackActionSchema,
  assessmentResults: z.array(
    z.object({
      id: z.string(),
      data: updateAssessmentResultOptionsSchema,
    }),
  ),
});
export type UpdateSessionFeedbackOptions = z.infer<typeof updateSessionFeedbackOptionsSchema>;

export type BulkUpdateSessionFeedbacksOptions = z.infer<typeof bulkUpdateSessionFeedbacksOptionsSchema>;

export const bulkUpdateSessionFeedbackResultsSchema = z.object({
  errors: z.string().array(),
  successSessionIds: z.string().array(),
  failedSessionIds: z.string().array(),
});
export type BulkUpdateSessionFeedbackResults = z.infer<typeof bulkUpdateSessionFeedbackResultsSchema>;

export const createOnDemandGLASchema = z.object({
  sourceSessionId: z.string().nullable().optional(),
  futureSessionId: z.string(),
  studentId: z.string(),
  coachId: z.string().nullable().optional(),
  lessonId: z.string(),
  recordedStatus: onDemandGLARecordedStatusSchema.optional(),
});
export type CreateOnDemandGLAOptions = z.infer<typeof createOnDemandGLASchema>;

export const onDemandGLASchema = z.object({
  id: z.string(),
  sourceSessionId: z.string().nullable(),
  futureSessionId: z.string(),
  studentId: z.string(),
  coachId: z.string().nullable(),
  lessonId: z.string(),
  recordedStatus: onDemandGLARecordedStatusSchema,
  createdAtInstant: z.instanceof(Temporal.Instant),
  updatedAtInstant: z.instanceof(Temporal.Instant),
});
export type OnDemandGLA = z.infer<typeof onDemandGLASchema>;

export const onDemandGLAWithLessonSchema = onDemandGLASchema.extend({
  lesson: lessonSchema,
});
export type OnDemandGLAWithLesson = z.infer<typeof onDemandGLAWithLessonSchema>;

export const onDemandGLAWithSessionFeedbackSchema = onDemandGLASchema.extend({
  sessionFeedback: z
    .object({
      sessionId: z.string(),
      coachId: z.string(),
      studentId: z.string(),
      studentAttended: z.boolean(),
      glaAssessmentCompleted: z.boolean(),
    })
    .nullable(),
  sourceSessionStartInstant: z.instanceof(Temporal.Instant).nullable(),
  onDemandGLASessionStartInstant: z.instanceof(Temporal.Instant),
  onDemandGLASessionId: z.string(),
});
export type OnDemandGLAWithSessionFeedback = z.infer<typeof onDemandGLAWithSessionFeedbackSchema>;

export const getSessionFeedbacksFilterSchema = z.object({
  sessionId: z.string().optional(),
  sessionIds: z.string().array().optional(),
  studentId: z.string().optional(),
  coachId: z.string().optional(),
  lessonId: z.string().optional(),
  query: z.string().optional(),
  feedbackSubmitted: z.coerce.boolean().optional(),
  studentAttended: z.coerce.boolean().nullable().optional(),
  grades: z.lazy(() => z.array(studentYearSchema).optional()),
  schoolIds: z.string().array().optional(),
  orgIds: z.string().array().optional(),
  startOnOrAfterPlainDateInUSCentral: z.string().optional(),
  startOnOrBeforePlainDateInUSCentral: z.string().optional(),
  lessonKinds: z.array(lessonKindSchema).optional(),
});
export type GetSessionFeedbacksFilter = z.infer<typeof getSessionFeedbacksFilterSchema>;

// Legacy schema - array of individual feedback updates
export const bulkUpdateSessionFeedbacksLegacyOptionsSchema = z.array(updateSessionFeedbackOptionsSchema);

// Common update properties for bulk operations (attendance updates)
export const bulkUpdateSessionFeedbackPropertiesSchema = z.object({
  studentAttended: z.boolean().optional(),
  lateFeedbackDismissed: z.boolean().optional(),
});

// Schema for bulk updating all feedbacks matching filter criteria
export const bulkUpdateAllSessionFeedbacksOptionsSchema = z.object({
  filters: getSessionFeedbacksFilterSchema,
  options: bulkUpdateSessionFeedbackPropertiesSchema,
});

// Union of schemas - supports legacy array format and new filter-based format
export const bulkUpdateSessionFeedbacksOptionsSchema = z.union([
  bulkUpdateSessionFeedbacksLegacyOptionsSchema,
  bulkUpdateAllSessionFeedbacksOptionsSchema,
]);

export const getSessionFeedbacksModifierSchema = DEPRECATED_pagingSchemaWithMaxPageSize.merge(
  filteringSchemaFrom(getSessionFeedbacksFilterSchema),
).extend({
  isAllPages: z.coerce.boolean().optional(),
});
export type GetSessionFeedbacksModifier = z.infer<typeof getSessionFeedbacksModifierSchema>;

export const getSessionFeedbackOptionsSchema = z.object({
  sessionId: z.string(),
  studentId: z.string(),
  coachId: z.string(),
});
export type GetSessionFeedbackOptions = z.infer<typeof getSessionFeedbackOptionsSchema>;

export const recordSessionFeedbackTimeTrackingBodySchema = z.object({
  sessionId: z.string(),
  coachId: z.string(),
  studentId: z.string(),
});
export type RecordSessionFeedbackTimeTrackingBody = z.infer<typeof recordSessionFeedbackTimeTrackingBodySchema>;

export const getAssignedSessionFeedbacksFilterSchema = getSessionFeedbacksFilterSchema.pick({
  coachId: true,
  lessonId: true,
  feedbackSubmitted: true,
  query: true,
});
export type GetAssignedSessionFeedbacksFilter = z.infer<typeof getAssignedSessionFeedbacksFilterSchema>;

export const getAssignedSessionFeedbacksModifierSchema = pagingSchema.merge(
  filteringSchemaFrom(getAssignedSessionFeedbacksFilterSchema),
);
export type GetAssignedSessionFeedbacksModifier = z.infer<typeof getAssignedSessionFeedbacksModifierSchema>;

export const coachViewableSessionFeedbackSchema = z.object({
  sessionId: z.string(),
  coachId: z.string(),
  studentId: z.string(),
  majorConcerns: z.string().nullable(),
  otherTechIssues: z.string().nullable(),
  studentAttended: z.boolean().nullable(),
  assessmentFinished: z.boolean().nullable(),
  feedbackSubmitted: z.boolean(),
  issues: sessionFeedbackIssueSchema.array(),
  feedbackContent: z.string().nullable(),
  submittedAtInstant: z.instanceof(Temporal.Instant).nullable(),
  draftSavedAtInstant: z.instanceof(Temporal.Instant).nullable(),
  lateFeedbackDismissed: z.boolean().nullable(),
  assessmentResults: z.array(assessmentResultSchema),
  coach: z.lazy(() => userSchema),
  student: z.lazy(() => coachViewableStudentSchema),
  session: z.object({
    id: z.string(),
    startInstant: z.instanceof(Temporal.Instant),
    endInstant: z.instanceof(Temporal.Instant),
    canceledAtInstant: z.instanceof(Temporal.Instant).nullable(),
    hoursCanceledBeforeStart: z.number().nullable(),
    lessonName: z.string(),
  }),
  relevantAssessments: z.array(assessmentWithContentSchema),
  submittedHoursFromSessionEnd: z.number().nullable(),
  onDemandGLA: onDemandGLAWithLessonSchema.nullable(),
  /**
   * Makeup diagnostic resume: assessment content IDs completed in a prior incomplete diagnostic.
   * Empty/omitted for non-makeup sessions.
   */
  diagnosticContentIdsCompletedInPriorSession: z.array(z.string()).optional(),
});

export type CoachViewableSessionFeedback = z.infer<typeof coachViewableSessionFeedbackSchema>;

export const coachViewableSessionFeedbacksSchema = paginatedResponse.extend({
  sessionFeedbacks: z.array(coachViewableSessionFeedbackSchema),
});

export type CoachViewableSessionFeedbacks = z.infer<typeof coachViewableSessionFeedbacksSchema>;

export const feedbackSubmittedStatusSchema = z.enum(['not_submitted', 'sent', 'sent_over_24h']);
export type FeedbackSubmittedStatus = z.infer<typeof feedbackSubmittedStatusSchema>;
export const FeedbackSubmittedStatus = feedbackSubmittedStatusSchema.enum;

export const getCoachViewableSessionFeedbacksFilterSchema = z.object({
  query: z.string().optional(),
  feedbackSubmitted: feedbackSubmittedStatusSchema.optional(),
});
export type GetCoachViewableSessionFeedbacksFilter = z.infer<typeof getCoachViewableSessionFeedbacksFilterSchema>;

export const getCoachViewableSessionFeedbacksModifierSchema = pagingSchema.merge(
  filteringSchemaFrom(getCoachViewableSessionFeedbacksFilterSchema),
);
export type GetCoachViewableSessionFeedbacksModifier = z.infer<typeof getCoachViewableSessionFeedbacksModifierSchema>;

export const SessionFeedbackIssueDetails: Record<SessionFeedbackIssue, { label: string; description: string }> = {
  [SessionFeedbackIssue.Too_easy]: {
    label: 'Too easy',
    description: 'Bored and disengaged',
  },
  [SessionFeedbackIssue.Too_hard]: {
    label: 'Too hard',
    description: 'Struggled and frustrated',
  },
  [SessionFeedbackIssue.Audio_issues]: {
    label: 'Audio (cannot hear)',
    description: 'Cannot hear. Multiple classes.',
  },
  [SessionFeedbackIssue.Connection_issues]: {
    label: 'Connection (lag, freezing)',
    description: 'Cannot connect. Multiple classes.',
  },
  [SessionFeedbackIssue.Video_issues]: {
    label: 'Video (cannot see)',
    description: 'Cannot see. Multiple classes.',
  },
  [SessionFeedbackIssue.Other_tech_issues]: {
    label: 'Other',
    description: 'Other tech issues',
  },
  [SessionFeedbackIssue.Strong_behavior_issues]: {
    label: 'Major class concerns',
    description: 'Major class concerns (see comments)',
  },
  [SessionFeedbackIssue.Constantly_late]: {
    label: 'Constantly late',
    description: 'More than 10 mins late',
  },
  [SessionFeedbackIssue.Extremely_noisy]: {
    label: 'Extremely noisy',
    description: 'Cannot hear. Cannot teach.',
  },
  [SessionFeedbackIssue.other]: {
    label: 'Other',
    description: 'Other issues not listed',
  },
  [SessionFeedbackIssue.late]: {
    label: 'Late (10 min or more)',
    description: 'Student arrived 10 minutes or more late to the session',
  },
  [SessionFeedbackIssue.left_early]: {
    label: 'Left early (10 min or more)',
    description: 'Student left 10 minutes or more early from the session',
  },
  [SessionFeedbackIssue.tech_issue]: {
    label: 'Tech issue (10 min or more)',
    description: 'A technical issue that affected the session',
  },
  [SessionFeedbackIssue.disruptive_or_disrespectful]: {
    label: 'Extremely disruptive or disrespectful',
    description: 'Student was extremely disruptive or disrespectful during the session',
  },
  [SessionFeedbackIssue.extreme_concerns]: {
    label: 'Extreme concerns (self harm, child abuse, etc.)',
    description: 'Extreme concerns about the student behavior or performance',
  },
};

export const toSessionFeedbackCompoundId = ({ sessionId, studentId, coachId }: GetSessionFeedbackOptions) =>
  `${sessionId}__${studentId}__${coachId}`;

/** Student/family portal row for Handouts & feedback. */
export const studentHandoutFeedbackRowSchema = z.object({
  sessionId: z.string(),
  coachId: z.string(),
  studentId: z.string(),
  sessionStartInstant: z.instanceof(Temporal.Instant),
  teacherDisplayName: z.string(),
  feedbackContent: z.string().nullable(),
  handouts: z.array(z.string()),
});
export type StudentHandoutFeedbackRow = z.infer<typeof studentHandoutFeedbackRowSchema>;

export const getMyStudentHandoutFeedbacksResultsSchema = paginatedResponse.extend({
  rows: z.array(studentHandoutFeedbackRowSchema),
});
export type GetMyStudentHandoutFeedbacksResults = z.infer<typeof getMyStudentHandoutFeedbacksResultsSchema>;

export const getMyStudentHandoutFeedbacksModifierSchema = largePagingSchema;
export type GetMyStudentHandoutFeedbacksModifier = z.infer<typeof getMyStudentHandoutFeedbacksModifierSchema>;
